"use client";

import React from "react";
import { 
  FileSpreadsheet, 
  Sliders, 
  Eye, 
  Coins, 
  ShieldAlert, 
  Bot, 
  CheckCircle2, 
  AlertTriangle,
  TrendingUp,
  Sparkles
} from "lucide-react";
import { StatusBadge } from "./technical-components";

export function MissionProblemSection() {
  const problems = [
    {
      id: "01",
      title: "SUBJECTIVE PE & PAPER CLIPBOARDS",
      vulnerability: "UNSTANDARDIZED EVALUATION",
      vulnerabilityColor: "text-amber-700 bg-amber-50 border-amber-200",
      reality: "Physical fitness is recorded on fragile paper clipboards or lost spreadsheets. No standardized benchmarks exist; subjective teacher mood determines team selection, causing natural athletic talent to go unmeasured.",
      solution: "Standardized Eurofit 6-Station Battery",
      solutionDetail: "Automated percentile ranking across 6 standardized stations with 6-axis biometric radars and longitudinal fitness tracking across 12 academic terms.",
      metric: "100% Objective Telemetry",
      icon: FileSpreadsheet,
    },
    {
      id: "02",
      title: "MATURATION BIAS (RELATIVE AGE EFFECT)",
      vulnerability: "AGE-BASED TALENT LOSS",
      vulnerabilityColor: "text-rose-700 bg-rose-50 border-rose-200",
      reality: "Children born early in the year dominate rosters simply because they are currently taller. High-ceiling late bloomers are mislabeled as untalented and permanently drop out of sports before their peak.",
      solution: "Mirwald Somatic Biological Age Algorithm",
      solutionDetail: "Calculates Peak Height Velocity (PHV) offset non-invasively, separating chronological age from biological maturity so future champions aren't cut early.",
      metric: "Biological Maturity vs Calendar Age",
      icon: Sliders,
    },
    {
      id: "03",
      title: "THE PARENT ATHLETIC BLACK BOX",
      vulnerability: "ZERO HEALTH VISIBILITY",
      vulnerabilityColor: "text-orange-700 bg-orange-50 border-orange-200",
      reality: "Parents receive exhaustive academic marksheets, but know zero about their child's cardiovascular capacity, motor agility, physical growth curve, or AAP clinical fitness status.",
      solution: "Digital Child Sports Report Card & Passport",
      solutionDetail: "Automated mobile report card tracking VO2 max, motor skills, AAP medical clearances, and growth curves under verifiable DPDP Act 2023 parent consent.",
      metric: "Quarterly Athletic Marksheet",
      icon: Eye,
    },
    {
      id: "04",
      title: "IDLE CAMPUS INFRASTRUCTURE",
      vulnerability: "LOCKED CAPITAL & ₹0 YIELD",
      vulnerabilityColor: "text-red-700 bg-red-50 border-red-200",
      reality: "World-class synthetic tracks, FIFA turfs, and pools sit locked and completely dark from 3:30 PM to 9:30 PM, generating ₹0 return on crores of invested school capital.",
      solution: "After-Hours Facility Marketplace ERP",
      solutionDetail: "Opens unused evening & weekend slots to verified community athletes, yielding ₹3L–₹8L in monthly recurring passive income for the school.",
      metric: "₹36L–₹96L Recurring Annual Income",
      icon: Coins,
    },
    {
      id: "05",
      title: "SECURITY & LIABILITY ANXIETIES",
      vulnerability: "CAMPUS SAFETY CONCERNS",
      vulnerabilityColor: "text-purple-700 bg-purple-50 border-purple-200",
      reality: "School boards fear renting grounds due to trespassing, property damage, equipment theft, and unauthorized strangers on sensitive school property.",
      solution: "Cryptographic QR Gate Passes & Escrow",
      solutionDetail: "HMAC-SHA256 single-use mobile QR gate passes, offline guard verification, pre/post-use condition photo audits, and zero-liability escrow deposits.",
      metric: "Zero-Trust Perimeter Defense",
      icon: ShieldAlert,
    },
    {
      id: "06",
      title: "GENERIC DRILLS & BLIND HOME TRAINING",
      vulnerability: "LACK OF PERSONALIZATION",
      vulnerabilityColor: "text-blue-700 bg-blue-50 border-blue-200",
      reality: "40 students wait in line for one ball during a 40-minute PE period. Zero individual feedback, no customized recovery or nutrition tracking, and no athletic guidance outside school.",
      solution: "24/7 AI Sports Coach & Training Planner",
      solutionDetail: "Interactive AI companion that analyzes student biometric data, plans daily customized drills & nutrition routines, tracks rest, and answers sports questions in real-time.",
      metric: "1-on-1 AI Athletic Mentorship",
      icon: Bot,
    },
  ];

  return (
    <section id="mission" className="py-24 border-b border-[#3A3A38]/20 bg-[#F7F7F5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <StatusBadge text="PROBLEM STATEMENT // THE 6 CAMPUS FAILURES" />
          <h2 className="font-space font-bold text-4xl sm:text-6xl text-[#1A3C2B] tracking-tight uppercase leading-[0.95]">
            THE 6 STRUCTURAL FAILURES IN SCHOOL SPORTS.
          </h2>
          <p className="font-mono-tech text-xs text-[#3A3A38] uppercase tracking-wider leading-relaxed">
            SCHOOLS INVEST CRORES INTO ATHLETIC INFRASTRUCTURE, YET PHYSICAL EDUCATION REMAINS TRAPPED IN PAPER-BASED GUESSWORK. HERE IS HOW OLYMPIO SCIENTIFICALLY TRANSFORMS IT.
          </p>
        </div>

        {/* 6 Problems Bento Grid (3 columns on desktop, 2 on tablet, 1 on mobile) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {problems.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.id}
                className="bg-white border border-[#3A3A38]/20 p-6 space-y-5 flex flex-col justify-between hover:border-[#1A3C2B] hover:shadow-lg transition-all duration-300 relative group"
              >
                {/* L-Corner Markers */}
                <div className="absolute top-0 left-0 w-2 h-2 border-t-2 border-l-2 border-[#1A3C2B] opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute top-0 right-0 w-2 h-2 border-t-2 border-r-2 border-[#1A3C2B] opacity-0 group-hover:opacity-100 transition-opacity" />

                <div className="space-y-4">
                  {/* Card Header */}
                  <div className="flex items-center justify-between border-b border-[#3A3A38]/10 pb-2.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#1A3C2B] font-mono-tech font-bold uppercase tracking-widest bg-[#1A3C2B]/10 px-2 py-0.5">
                        FAILURE_{item.id}
                      </span>
                    </div>
                    <div className="w-8 h-8 bg-[#1A3C2B]/5 flex items-center justify-center text-[#1A3C2B] group-hover:bg-[#1A3C2B] group-hover:text-white transition-colors">
                      <Icon className="w-4 h-4" />
                    </div>
                  </div>

                  {/* Vulnerability Pill */}
                  <div className="flex items-center gap-1.5">
                    <AlertTriangle className="w-3 h-3 text-[#FF8C69]" />
                    <span className="font-mono-tech text-[10px] font-bold text-[#FF8C69] uppercase tracking-wider">
                      {item.vulnerability}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="font-space font-bold text-lg text-[#1A3C2B] uppercase tracking-tight leading-snug">
                    {item.title}
                  </h3>

                  {/* Legacy Reality */}
                  <div className="space-y-1.5 font-mono-tech text-xs bg-[#F7F7F5] p-3 border-l-2 border-[#FF8C69]">
                    <span className="text-[10px] text-[#3A3A38]/70 font-bold uppercase block">
                      // LEGACY REALITY:
                    </span>
                    <p className="text-[#3A3A38] leading-relaxed text-[11px]">
                      {item.reality}
                    </p>
                  </div>
                </div>

                {/* Olympio Solution Upgrade */}
                <div className="border border-[#1A3C2B]/30 bg-[#1A3C2B]/5 p-4 space-y-2 font-mono-tech">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-[#1A3C2B] uppercase">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#1A3C2B] shrink-0" />
                      <span className="truncate">{item.solution}</span>
                    </div>
                  </div>
                  
                  <p className="text-[11px] text-[#3A3A38] leading-relaxed">
                    {item.solutionDetail}
                  </p>

                  <div className="pt-2 border-t border-[#1A3C2B]/15 flex items-center justify-between text-[10px] font-bold text-[#1A3C2B]">
                    <span className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3 text-[#1A3C2B]" />
                      OLYMPIO IMPACT:
                    </span>
                    <span className="bg-[#1A3C2B] text-white px-2 py-0.5 uppercase tracking-wider">
                      {item.metric}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Banner */}
        <div className="border border-[#1A3C2B]/30 bg-white p-6 flex flex-col md:flex-row items-center justify-between gap-4 font-mono-tech text-xs shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-[#1A3C2B]" />
            <span className="text-[#1A3C2B] font-bold uppercase tracking-wider">
              READY TO REPLACE OBSOLETE SPORTS PRACTICES WITH SCIENTIFIC INTELLIGENCE?
            </span>
          </div>
          <a
            href="#contact"
            className="px-6 py-2.5 bg-[#1A3C2B] text-white hover:bg-[#132d20] transition uppercase font-bold text-xs tracking-wider flex items-center gap-2"
          >
            <span>SCHEDULE CAMPUS AUDIT &amp; DEMO</span>
            <Sparkles className="w-3.5 h-3.5 text-[#9EFFBF]" />
          </a>
        </div>
      </div>
    </section>
  );
}

